import React, { useState, useRef, useEffect } from "react";
import "./CustomInputStyle.scss";
import EyeHideIcon from "../../../assets/icons/Eye-Hide";
import EyeShowIcon from "../../../assets/icons/Eye-Show";
import ChevronUpIcon from "../../../assets/icons/Chevron-Up";
import ChevronDownIcon from "../../../assets/icons/Chevron-Down";
import TrashCanIcon from "../../../assets/icons/Trash-Can";
import { motion, AnimatePresence } from "framer-motion";

const CustomInput = ({
  fieldType,
  label,
  id,
  type = "text",
  name,
  onChange,
  options,
  placeholder,
  defaultValue,
  isDisabled = false,
  isInvalid = false,
  isRequired = false,
  isMultiple = false,
  selectedOptions = [],
  minDate,
  maxDate,
  rows = "5",
  cols,
  startContent = true,
  endContent = true,
  startIcon,
  endIcon,
  helperText,
  errorText,
  className,
  inputFieldBoxClass,
  inputLabelTextClass,
  inputFieldClass,
  selectFieldClass,
  selectOptionsClass,
  startIconClass,
  endIconClass,
  passwordHideShowClass,
  helperTextClass,
  errorTextClass,
  dropdownMenuClass,
  dropdownMenuInnerClass,
  dropdownItemClass,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const requireString = "*";

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedOptions);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const dropdownRef = useRef(null);

  // Handle outside click to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTimeout(() => {
          setIsOpen(false);
        }, 200);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle dynamic positioning of dropdown
  useEffect(() => {
    if (dropdownRef.current) {
      const { bottom, top } = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - bottom;
      const spaceAbove = top;

      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (option) => {
    if (isMultiple) {
      const isAlreadySelected = selected.some(
        (item) => item.value === option.value
      );
      const updatedSelection = isAlreadySelected
        ? selected.filter((item) => item.value !== option.value)
        : [...selected, option];

      setSelected(updatedSelection);

      if (onChange) {
        onChange(updatedSelection);
      }
    } else {
      setSelected([option]);
      setIsOpen(false);

      if (onChange) {
        onChange([option]);
      }
    }
  };

  const isOptionSelected = (option) => {
    return selected.some((item) => item.value === option.value);
  };

  const handleDeselectAll = () => {
    setSelected([]);
    if (onChange) {
      onChange([]);
    }
  };

  const dropdownVariants = {
    open: { opacity: 1, scale: 1 },
    closed: { opacity: 0, scale: 0 },
  };

  switch (fieldType) {
    case "textField": {
      return (
        <div className={`form-control ${className}`}>
          {label && (
            <label
              htmlFor={id}
              className={`input-label-text ${inputLabelTextClass}`}
            >
              {label}
              {isRequired && (
                <span className="required-star">{requireString}</span>
              )}
            </label>
          )}
          <div className={`input-field-box ${inputFieldBoxClass}`}>
            {startContent &&
              startIcon &&
              React.isValidElement(startIcon) &&
              React.cloneElement(startIcon, {
                className: `start-icon ${startIconClass}`,
              })}
            <input
              id={id}
              type={type}
              name={name}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              placeholder={placeholder}
              defaultValue={defaultValue}
              className={`input-field ${inputFieldClass}`}
              onChange={onChange}
              {...rest}
            />
            {endContent &&
              endIcon &&
              React.isValidElement(endIcon) &&
              React.cloneElement(endIcon, {
                className: `end-icon ${endIconClass}`,
              })}
          </div>
          {helperText && (
            <span className={`helper-text ${helperTextClass}`}>
              {helperText}
            </span>
          )}
          {errorText && (
            <span className={`error-text ${errorTextClass}`}>{errorText}</span>
          )}
        </div>
      );
    }

    case "passwordField": {
      return (
        <div className={`form-control ${className}`}>
          {label && (
            <label
              htmlFor={id}
              className={`input-label-text ${inputLabelTextClass}`}
            >
              {label}
              {isRequired && (
                <span className="required-star">{requireString}</span>
              )}
            </label>
          )}
          <div className={`input-field-box ${inputFieldBoxClass}`}>
            {startContent &&
              startIcon &&
              React.isValidElement(startIcon) &&
              React.cloneElement(startIcon, {
                className: `start-icon ${startIconClass}`,
              })}
            <input
              id={id}
              type={showPassword ? "text" : "password"}
              name={name}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              placeholder={placeholder}
              defaultValue={defaultValue}
              className={`input-field ${inputFieldClass}`}
              onChange={onChange}
              {...rest}
            />
            {endContent &&
              endIcon &&
              React.isValidElement(endIcon) &&
              React.cloneElement(endIcon, {
                className: `end-icon ${endIconClass}`,
              })}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`password-hide-show-btn ${passwordHideShowClass}`}
            >
              {showPassword ? <EyeHideIcon /> : <EyeShowIcon />}
            </button>
          </div>
          {helperText && (
            <span className={`helper-text ${helperTextClass}`}>
              {helperText}
            </span>
          )}
          {errorText && (
            <span className={`error-text ${errorTextClass}`}>{errorText}</span>
          )}
        </div>
      );
    }

    case "textAreaField": {
      return (
        <div className={`form-control ${className}`}>
          {label && (
            <label
              htmlFor={id}
              className={`input-label-text ${inputLabelTextClass}`}
            >
              {label}
              {isRequired && (
                <span className="required-star">{requireString}</span>
              )}
            </label>
          )}
          <div className={`input-field-box ${inputFieldBoxClass}`}>
            {startContent &&
              startIcon &&
              React.isValidElement(startIcon) &&
              React.cloneElement(startIcon, {
                className: `start-icon ${startIconClass}`,
              })}
            <textarea
              id={id}
              name={name}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              placeholder={placeholder}
              defaultValue={defaultValue}
              className={`textarea-field ${inputFieldClass}`}
              onChange={onChange}
              {...rest}
            />
            {endContent &&
              endIcon &&
              React.isValidElement(endIcon) &&
              React.cloneElement(endIcon, {
                className: `end-icon ${endIconClass}`,
              })}
          </div>
          {helperText && (
            <span className={`helper-text ${helperTextClass}`}>
              {helperText}
            </span>
          )}
          {errorText && (
            <span className={`error-text ${errorTextClass}`}>{errorText}</span>
          )}
        </div>
      );
    }

    case "selectField": {
      return (
        <div className={`form-control ${className}`} ref={dropdownRef}>
          {label && (
            <label
              htmlFor={id}
              className={`input-label-text ${inputLabelTextClass}`}
            >
              {label}
              {isRequired && (
                <span className="required-star">{requireString}</span>
              )}
            </label>
          )}
          <div className={`input-field-box ${inputFieldBoxClass}`}>
            {startContent &&
              startIcon &&
              React.isValidElement(startIcon) &&
              React.cloneElement(startIcon, {
                className: `start-icon ${startIconClass}`,
              })}
            <div
              id={id}
              isDisabled={isDisabled}
              isRequired={isRequired}
              onClick={toggleDropdown}
              className={`select-field ${selectFieldClass} ${
                isOpen ? "select-field-open" : ""
              }`}
            >
              <div className={`selected-options ${selectOptionsClass}`}>
                {selected.length > 0
                  ? selected.map((item, index) => (
                      <span
                        key={index}
                        className={`${
                          selected.length === 1
                            ? "selected-option-text-single"
                            : "selected-option-text"
                        }`}
                      >
                        {item.flag && (
                          <img
                            src={item.flag}
                            alt={item.label}
                            className="country-flag"
                          />
                        )}
                        {item.dialCode && (
                          <span className="dial-code-text">
                            {item.dialCode}
                          </span>
                        )}
                        {item.label && (
                          <span className="label-text">{item.label}</span>
                        )}
                        {index < selected.length - 1 && ""}
                      </span>
                    ))
                  : "Select option(s)"}
              </div>
              <div className="dropdown-arrow">
                {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>
            {endContent &&
              endIcon &&
              React.isValidElement(endIcon) &&
              React.cloneElement(endIcon, {
                className: `end-icon ${endIconClass}`,
              })}

            {isOpen && (
              <AnimatePresence>
                <motion.div
                  className={`dropdown-menu ${dropdownMenuClass} ${
                    dropdownPosition === "top"
                      ? "dropdown-top"
                      : "dropdown-bottom"
                  }`}
                  key="dropdown"
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={dropdownVariants}
                >
                  <div
                    className={`dropdown-menu-inner ${dropdownMenuInnerClass}`}
                  >
                    {options.map((option) => (
                      <div
                        key={option.value}
                        className={`dropdown-item ${dropdownItemClass} ${
                          isOptionSelected(option) ? "selected" : ""
                        }`}
                        onClick={() => handleSelect(option)}
                      >
                        {option.flag && (
                          <span className="icon-box">
                            <img
                              src={option.flag}
                              alt={option.label}
                              className="country-flag"
                            />
                          </span>
                        )}
                        <span className="text">
                          {option.dialCode && (
                            <span className="dial-code-text">
                              {option.dialCode}
                            </span>
                          )}
                          {option.label && (
                            <span className="label-text">{option.label}</span>
                          )}
                        </span>
                        {isMultiple && isOptionSelected(option) && (
                          <span
                            className="deselect-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelect(option);
                            }}
                          >
                            <TrashCanIcon />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {isMultiple && selected.length > 0 && (
                    <button
                      className="deselect-all-btn"
                      onClick={handleDeselectAll}
                    >
                      <TrashCanIcon />
                      Deselect All
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
          {helperText && (
            <span className={`helper-text ${helperTextClass}`}>
              {helperText}
            </span>
          )}
          {errorText && (
            <span className={`error-text ${errorTextClass}`}>{errorText}</span>
          )}
        </div>
      );
    }
  }
};

export default CustomInput;
